'use client'

import Box from '@mui/material/Box'
import { motion } from 'framer-motion'
import { useEffect, useState } from 'react'

interface Candle {
  x: number
  open: number
  close: number
  high: number
  low: number
}

export default function AnimatedTradingChart() {
  const [candles, setCandles] = useState<Candle[]>([])
  const [progress, setProgress] = useState(0)

  useEffect(() => {
    const numCandles = 25
    const generated: Candle[] = []
    let basePrice = 100

    for (let i = 0; i < numCandles; i++) {
      const volatility = 9
      const trend = Math.sin(i / 8) * 6
      const open = basePrice
      const closeChange = (Math.random() - 0.45) * volatility + trend
      const close = open + closeChange
      const high = Math.max(open, close) + Math.random() * 6
      const low = Math.min(open, close) - Math.random() * 6
      generated.push({ x: i, open, close, high, low })
      basePrice = close
    }

    setCandles(generated)

    const interval = setInterval(() => {
      setProgress((p) => Math.min(p + 0.02, 1))
    }, 30)

    return () => clearInterval(interval)
  }, [])

  const width = 1200
  const height = 400
  const pad = { top: 40, right: 0, bottom: 40, left: 0 }
  const cw = width - pad.left - pad.right
  const ch = height - pad.top - pad.bottom

  const allPrices = candles.flatMap((c) => [c.high, c.low])
  const minP = Math.min(...allPrices) - 5
  const maxP = Math.max(...allPrices) + 5
  const range = maxP - minP

  const getX = (i: number) => pad.left + (i / (candles.length - 1)) * cw
  const getY = (p: number) => pad.top + ((maxP - p) / range) * ch
  const candleW = (cw / candles.length) * 0.7
  const visible = Math.floor(candles.length * progress)

  return (
    <motion.section
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4 }}
      style={{ position: 'relative', width: '100%' }}
    >
      <Box
        sx={{
          width: '100%',
          height: { xs: 180, sm: 220, lg: 280 },
          background:
            'linear-gradient(180deg, #ffffff 0%, #fafafa 50%, #ffffff 100%)',
        }}
      >
        <svg
          width='100%'
          height='100%'
          viewBox={`0 0 ${width} ${height}`}
          preserveAspectRatio='none'
          style={{ display: 'block' }}
        >
          <defs>
            <filter
              id='glow-cyan'
              x='-50%'
              y='-50%'
              width='200%'
              height='200%'
              colorInterpolationFilters='sRGB'
            >
              <feGaussianBlur stdDeviation='3' result='coloredBlur' />
              <feMerge>
                <feMergeNode in='coloredBlur' />
                <feMergeNode in='SourceGraphic' />
              </feMerge>
            </filter>
            <filter
              id='glow-dark'
              x='-50%'
              y='-50%'
              width='200%'
              height='200%'
              colorInterpolationFilters='sRGB'
            >
              <feGaussianBlur stdDeviation='3' result='coloredBlur' />
              <feMerge>
                <feMergeNode in='coloredBlur' />
                <feMergeNode in='SourceGraphic' />
              </feMerge>
            </filter>
          </defs>

          {/* Grid lines */}
          {[0, 0.25, 0.5, 0.75, 1].map((ratio) => (
            <line
              key={ratio}
              x1={pad.left}
              y1={pad.top + ch * ratio}
              x2={pad.left + cw}
              y2={pad.top + ch * ratio}
              stroke='#e5e7eb'
              strokeWidth='1'
              opacity='0.3'
            />
          ))}

          {/* Candlesticks */}
          {candles.slice(0, visible).map((candle, i) => {
            const x = getX(i)
            const yOpen = getY(candle.open)
            const yClose = getY(candle.close)
            const yHigh = getY(candle.high)
            const yLow = getY(candle.low)
            const isGreen = candle.close > candle.open
            const bodyH = Math.abs(yClose - yOpen)

            return (
              <motion.g
                key={i}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{
                  duration: 0.4,
                  delay: i * 0.04,
                }}
              >
                <motion.line
                  x1={x}
                  y1={yHigh}
                  x2={x}
                  y2={yLow}
                  stroke={isGreen ? '#06b6d4' : '#0f172a'}
                  strokeWidth='2'
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 0.7 }}
                  transition={{ duration: 0.25, delay: i * 0.04 + 0.1 }}
                />
                <motion.rect
                  x={x - candleW / 2}
                  y={Math.min(yOpen, yClose)}
                  width={candleW}
                  height={Math.max(bodyH, 2)}
                  fill={isGreen ? '#06b6d4' : '#0f172a'}
                  stroke={isGreen ? '#06b6d4' : '#0f172a'}
                  strokeWidth='1.5'
                  initial={{ opacity: 0, scaleY: 0 }}
                  animate={{ opacity: isGreen ? 0.85 : 0.75, scaleY: 1 }}
                  transition={{
                    duration: 0.3,
                    delay: i * 0.04,
                    scaleY: { type: 'spring', stiffness: 300, damping: 18 },
                  }}
                  style={{
                    transformOrigin: `${x}px ${Math.min(yOpen, yClose) + bodyH / 2}px`,
                  }}
                />
              </motion.g>
            )
          })}

          {/* Trend line */}
          <motion.path
            d={candles
              .slice(0, visible)
              .map((c, i) => {
                const x = getX(i)
                const y = getY((c.open + c.close) / 2)
                return `${i === 0 ? 'M' : 'L'} ${x} ${y}`
              })
              .join(' ')}
            stroke='#06b6d4'
            strokeWidth='2'
            fill='none'
            opacity='0.3'
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 1.2, ease: 'easeInOut' }}
          />
        </svg>
      </Box>
    </motion.section>
  )
}
